import { AlertTriangle, Info, Loader2 } from 'lucide-react'
import { cn, SURFACE_COLOR } from '@/lib/utils'

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden />
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded bg-[var(--bg-hover)]', className)}
      aria-hidden
    />
  )
}

export function Badge({
  children,
  tone = 'neutral',
  className,
  ...rest
}: {
  children: React.ReactNode
  tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'win'
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      {...rest}
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        tone === 'neutral' && 'bg-[var(--bg-hover)] text-muted',
        tone === 'accent' && 'bg-[var(--accent)]/15 text-accent',
        tone === 'warn' && 'bg-[var(--warn)]/15 text-warn',
        tone === 'danger' && 'bg-[var(--danger)]/15 text-danger',
        tone === 'win' && 'bg-[var(--win)]/15 text-win',
        className,
      )}
    >
      {children}
    </span>
  )
}

/** Surface is one of the few things allowed to carry colour, because it is a
 *  real dimension of the data rather than decoration. */
export function SurfaceDot({ surface }: { surface: string | null }) {
  if (!surface) return <span className="text-subtle">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 capitalize">
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ background: SURFACE_COLOR[surface] ?? 'var(--fg-subtle)' }}
        aria-hidden
      />
      {surface}
    </span>
  )
}

/**
 * A visible, consistent home for the dataset's gaps.
 *
 * This exists because a filter that silently drops two thirds of the sample is
 * how a user reaches a confident wrong conclusion. Coverage is a feature of the
 * product, not a footnote in the docs.
 */
export function DataCaveat({
  children,
  tone = 'info',
  className,
}: {
  children: React.ReactNode
  tone?: 'info' | 'warn'
  className?: string
}) {
  const Icon = tone === 'warn' ? AlertTriangle : Info
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
        tone === 'warn'
          ? 'border-[var(--warn)]/30 bg-[var(--warn)]/10 text-warn'
          : 'bg-[var(--bg-elevated)] text-muted',
        className,
      )}
    >
      <Icon className="mt-px size-3.5 shrink-0" aria-hidden />
      <div className="leading-relaxed">{children}</div>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <p className="text-sm font-medium text-fg">{title}</p>
      {description && (
        <p className="max-w-md text-sm text-muted leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function ErrorState({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <EmptyState
      title="Something went wrong"
      description={error.message}
      action={
        retry && (
          <button
            type="button"
            onClick={retry}
            className="text-sm text-accent hover:underline"
          >
            Try again
          </button>
        )
      }
    />
  )
}
